/**
 * ArubaUserCreationPage — view existing Aruba Central users + create new ones.
 *
 * Existing users render in a table with role badges + last-login + an
 * activate/deactivate toggle. The "Create user" form sits at the top in a
 * card, behind a small RHF + Zod gate so we never POST garbage.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertTriangle,
  ArrowRight,
  Mail,
  Search,
  ShieldCheck,
  UserCircle,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import { TableSkeleton } from "@/components/ui/skeleton";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useArubaUsers } from "@/stores/aruba-users";
import {
  ArubaUserCreateSchema,
  type ArubaUserCreate,
} from "@/api/schemas/aruba";
import { ApiError } from "@/api/client";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

const COLS = "1.4fr 1.2fr 0.8fr 1fr 0.9fr 0.7fr";

function roleBadge(role: string) {
  if (role === "admin")
    return (
      <Badge variant="info" size="sm">
        admin
      </Badge>
    );
  if (role === "operator")
    return (
      <Badge variant="ok" size="sm">
        operator
      </Badge>
    );
  return (
    <Badge variant="neutral" size="sm">
      {role}
    </Badge>
  );
}

function timeAgo(iso?: string | null) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function ArubaUserCreationPage() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, status, error, load, create, setActive } = useArubaUsers();
  useEffect(() => {
    void load();
  }, [load]);

  const [query, setQuery] = useState("");
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.fullName.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q),
    );
  }, [data, query]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ArubaUserCreate>({
    resolver: zodResolver(ArubaUserCreateSchema),
    defaultValues: {
      email: "",
      fullName: "",
      role: "operator",
      groupScope: "",
    },
  });

  async function onCreate(values: ArubaUserCreate) {
    try {
      await create(values);
      toast.success(`Created ${values.email}`);
      reset({ email: "", fullName: "", role: "operator", groupScope: "" });
    } catch (err) {
      toast.error("Unable to create user", {
        description:
          err instanceof ApiError
            ? `API error ${err.status}`
            : err instanceof Error
              ? err.message
              : "unknown",
      });
    }
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">
          Aruba
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">User creation</span>
      </div>

      <ArubaSubNav />

      <NoVendorKeysBanner vendor="aruba" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            User creation
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Aruba Central user accounts visible through the active key. Create
            new ones, deactivate departing employees, and audit who's signed in
            recently.
          </p>
        </div>
        <Badge variant="info" size="md">
          <ShieldCheck size={11} />
          {data.filter((u) => u.active).length} active ·{" "}
          {data.filter((u) => !u.active).length} disabled
        </Badge>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {/* Create user form */}
      <Card>
        <CardContent>
          <form
            noValidate
            onSubmit={(e) => void handleSubmit(onCreate)(e)}
            className="grid grid-cols-1 gap-3 lg:grid-cols-[1.3fr_1fr_0.6fr_1fr_auto] lg:items-end"
          >
            <div className="lg:col-span-5">
              <CardTitle>Create a new user</CardTitle>
              <CardSub>
                The user will get an Aruba Central invite via email.
              </CardSub>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cu-email">Email</Label>
              <div className="relative">
                <Mail
                  size={14}
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
                />
                <Input
                  id="cu-email"
                  type="email"
                  autoComplete="off"
                  placeholder="user@guest-tek.com"
                  className="pl-9"
                  invalid={!!errors.email}
                  {...register("email")}
                />
              </div>
              {errors.email ? (
                <span className="text-xs text-rose">
                  {errors.email.message}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cu-name">Full name</Label>
              <Input
                id="cu-name"
                placeholder="Eustache Duverna"
                invalid={!!errors.fullName}
                {...register("fullName")}
              />
              {errors.fullName ? (
                <span className="text-xs text-rose">
                  {errors.fullName.message}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cu-role">Role</Label>
              <select
                id="cu-role"
                {...register("role")}
                className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              >
                <option value="admin">Admin</option>
                <option value="operator">Operator</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cu-scope">Group scope</Label>
              <Input
                id="cu-scope"
                placeholder="* (all) or PAR-09 IDF, …"
                {...register("groupScope")}
              />
            </div>
            <Button type="submit" disabled={isSubmitting}>
              <UserPlus size={14} />
              {isSubmitting ? "Creating…" : "Create"}
              <ArrowRight size={13} />
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Existing users */}
      <Card>
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <CardTitle>Existing users</CardTitle>
            <CardSub className="mt-1">
              Active key sees {data.length} user{data.length === 1 ? "" : "s"}.
            </CardSub>
          </div>
          <div className="relative max-w-xs flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter users…"
              className="pl-9"
            />
          </div>
        </div>
        {status === "loading" && data.length === 0 ? (
          <TableSkeleton
            className="rounded-none border-0 border-t border-line"
            columns={COLS}
            columnCount={6}
            rows={6}
          />
        ) : (
          <TableShell className="rounded-none border-0 border-t border-line">
            <TableHeadRow columns={COLS}>
              <TableHeadCell>User</TableHeadCell>
              <TableHeadCell>Email</TableHeadCell>
              <TableHeadCell>Role</TableHeadCell>
              <TableHeadCell>Scope</TableHeadCell>
              <TableHeadCell>Last login</TableHeadCell>
              <TableHeadCell />
            </TableHeadRow>
            {visible.map((u) => (
              <TableRow
                key={u.email}
                columns={COLS}
                className={u.active ? "" : "opacity-60"}
              >
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-aurora-grad text-bg-0">
                      <UserCircle size={14} />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-fg">
                        {u.fullName}
                      </div>
                      {!u.active ? (
                        <div className="text-[10.5px] text-fg-mute">
                          deactivated
                        </div>
                      ) : null}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-[11px] text-fg-dim">
                  {u.email}
                </TableCell>
                <TableCell>{roleBadge(u.role)}</TableCell>
                <TableCell className="font-mono text-[10.5px] text-fg-dim">
                  {u.groupScope ?? "—"}
                </TableCell>
                <TableCell className="text-xs text-fg-dim">
                  {timeAgo(u.lastLoginAt)}
                </TableCell>
                <TableCell className="flex justify-end">
                  <Button
                    size="sm"
                    variant={u.active ? "ghost" : "primary"}
                    onClick={() => void setActive(u.email, !u.active)}
                  >
                    {u.active ? (
                      <>
                        <UserMinus size={13} />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <UserPlus size={13} />
                        Activate
                      </>
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-fg-mute">
                {query ? `No users match "${query}".` : "No users yet."}
              </div>
            ) : null}
          </TableShell>
        )}
      </Card>
    </div>
  );
}
